import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Admin Leave Requests Dashboard
 *
 * Displays ALL leave requests (pending and approved) for all employees
 * Admin-only page
 */
import { useQuery } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { Calendar, Clock, AlertCircle, User, CheckCircle, XCircle, Filter, Inbox, ChevronDown, ChevronRight, } from 'lucide-react';
import { useState, useMemo } from 'react';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Skeleton } from '../../components/ui/skeleton';
import { useEmployees } from '../../hooks/useEmployees';
import { config } from '../../lib/config';
import { useAuthStore, selectIsAdmin } from '../../stores/auth.store';
const LEAVE_API_URL = config.api.leave;
const LEAVE_TYPE_LABELS = {
    pto: 'PTO',
    vacation: 'Vacation',
    sick: 'Sick Leave',
    personal: 'Personal',
    bereavement: 'Bereavement',
    jury_duty: 'Jury Duty',
};
const STATUS_COLORS = {
    pending: 'bg-yellow-100 text-yellow-800',
    approved: 'bg-green-100 text-green-800',
    rejected: 'bg-red-100 text-red-800',
    cancelled: 'bg-gray-100 text-gray-800',
};
const STATUS_ICONS = {
    pending: Clock,
    approved: CheckCircle,
    rejected: XCircle,
    cancelled: AlertCircle,
};
/**
 * Get authorization headers
 */
function getAuthHeaders() {
    const tokens = useAuthStore.getState().tokens;
    const user = useAuthStore.getState().user;
    return {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${tokens?.idToken || ''}`,
        'X-Tenant-Id': user?.tenantId || '',
    };
}
/**
 * Fetch leave requests for a specific employee
 */
async function getEmployeeRequests(employeeId) {
    const response = await fetch(`${LEAVE_API_URL}/requests?employeeId=${employeeId}`, {
        method: 'GET',
        headers: getAuthHeaders(),
    });
    if (!response.ok) {
        // 404 means no requests exist yet
        if (response.status === 404) {
            return { requests: [], count: 0 };
        }
        throw new Error(`Failed to fetch requests: ${response.statusText}`);
    }
    return response.json();
}
export default function AdminLeaveRequests() {
    const isAdmin = useAuthStore(selectIsAdmin);
    const [statusFilter, setStatusFilter] = useState('all');
    const [expandedEmployees, setExpandedEmployees] = useState(new Set());
    // Fetch all employees
    const { data: employees, isLoading: loadingEmployees } = useEmployees();
    // Fetch requests for all active employees
    const { data: allRequestsData, isLoading: loadingRequests } = useQuery({
        queryKey: ['admin', 'leave', 'all-requests'],
        queryFn: async () => {
            if (!employees)
                return [];
            const activeEmployees = employees.filter(emp => emp.status === 'active');
            // Fetch requests for each employee in parallel
            const requestsPromises = activeEmployees.map(emp => getEmployeeRequests(emp.employeeId).catch(() => ({ requests: [], count: 0 })));
            const results = await Promise.all(requestsPromises);
            // Combine all requests with employee info
            const allRequests = results.flatMap((result, index) => {
                const employee = activeEmployees[index];
                if (!employee)
                    return [];
                return result.requests.map(req => ({
                    ...req,
                    employeeName: `${employee.firstName} ${employee.lastName}`,
                    employeePosition: employee.position || 'Employee',
                }));
            });
            return allRequests;
        },
        enabled: !!employees && isAdmin,
    });
    // Filter requests by status
    const filteredRequests = useMemo(() => {
        if (!allRequestsData)
            return [];
        if (statusFilter === 'all')
            return allRequestsData;
        return allRequestsData.filter(req => req.status === statusFilter);
    }, [allRequestsData, statusFilter]);
    // Group requests by employee
    const requestsByEmployee = useMemo(() => {
        const grouped = new Map();
        filteredRequests.forEach(req => {
            const key = req.employeeId;
            if (!grouped.has(key)) {
                grouped.set(key, []);
            }
            grouped.get(key).push(req);
        });
        // Sort requests within each employee group by date descending
        grouped.forEach(requests => {
            requests.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        });
        return grouped;
    }, [filteredRequests]);
    // Calculate summary stats
    const stats = useMemo(() => {
        if (!allRequestsData)
            return { pending: 0, approved: 0, totalHours: 0, employees: 0 };
        const pending = allRequestsData.filter(r => r.status === 'pending');
        const approved = allRequestsData.filter(r => r.status === 'approved');
        const totalHours = pending.reduce((sum, r) => sum + r.hoursRequested, 0);
        const uniqueEmployees = new Set(allRequestsData.map(r => r.employeeId)).size;
        return {
            pending: pending.length,
            approved: approved.length,
            totalHours,
            employees: uniqueEmployees,
        };
    }, [allRequestsData]);
    const toggleEmployee = (employeeId) => {
        setExpandedEmployees(prev => {
            const next = new Set(prev);
            if (next.has(employeeId)) {
                next.delete(employeeId);
            }
            else {
                next.add(employeeId);
            }
            return next;
        });
    };
    // Check admin access
    if (!isAdmin) {
        return (_jsxs("div", { className: "container mx-auto py-8 px-4", children: [_jsx("h1", { className: "text-3xl font-bold mb-8", children: "All Leave Requests" }), _jsxs(Alert, { variant: "destructive", children: [_jsx(AlertCircle, { className: "h-4 w-4" }), _jsx(AlertDescription, { children: "Only administrators can view all leave requests" })] })] }));
    }
    if (loadingEmployees || loadingRequests) {
        return (_jsxs("div", { className: "container mx-auto py-8 px-4", children: [_jsx("h1", { className: "text-3xl font-bold mb-8", children: "All Leave Requests" }), _jsxs("div", { className: "space-y-4", children: [_jsx(Skeleton, { className: "h-32" }), _jsx(Skeleton, { className: "h-32" }), _jsx(Skeleton, { className: "h-32" })] })] }));
    }
    return (_jsxs("div", { className: "container mx-auto py-8 px-4", children: [_jsxs("div", { className: "mb-8", children: [_jsx("h1", { className: "text-3xl font-bold", children: "All Leave Requests" }), _jsx("p", { className: "text-gray-600 mt-2", children: "View and monitor leave requests across the entire organization" })] }), _jsxs("div", { className: "grid gap-4 md:grid-cols-4 mb-8", children: [_jsxs(Card, { children: [_jsx(CardHeader, { className: "pb-3", children: _jsx(CardTitle, { className: "text-sm font-medium text-gray-600", children: "Pending Requests" }) }), _jsx(CardContent, { children: _jsx("div", { className: "text-3xl font-bold text-yellow-600", children: stats.pending }) })] }), _jsxs(Card, { children: [_jsx(CardHeader, { className: "pb-3", children: _jsx(CardTitle, { className: "text-sm font-medium text-gray-600", children: "Approved Requests" }) }), _jsx(CardContent, { children: _jsx("div", { className: "text-3xl font-bold text-green-600", children: stats.approved }) })] }), _jsxs(Card, { children: [_jsx(CardHeader, { className: "pb-3", children: _jsx(CardTitle, { className: "text-sm font-medium text-gray-600", children: "Pending Hours" }) }), _jsxs(CardContent, { children: [_jsx("div", { className: "text-3xl font-bold text-gray-900", children: stats.totalHours }), _jsxs("p", { className: "text-sm text-gray-600 mt-1", children: [(stats.totalHours / 8).toFixed(1), " days"] })] })] }), _jsxs(Card, { children: [_jsx(CardHeader, { className: "pb-3", children: _jsx(CardTitle, { className: "text-sm font-medium text-gray-600", children: "Employees with Requests" }) }), _jsx(CardContent, { children: _jsx("div", { className: "text-3xl font-bold text-gray-900", children: stats.employees }) })] })] }), _jsxs("div", { className: "mb-6 flex items-center gap-3", children: [_jsx(Filter, { className: "h-5 w-5 text-gray-400" }), _jsx("span", { className: "text-sm font-medium text-gray-700", children: "Filter by status:" }), _jsxs("div", { className: "flex gap-2", children: [_jsx(Button, { variant: statusFilter === 'all' ? 'default' : 'outline', size: "sm", onClick: () => setStatusFilter('all'), children: "All" }), _jsx(Button, { variant: statusFilter === 'pending' ? 'default' : 'outline', size: "sm", onClick: () => setStatusFilter('pending'), children: "Pending" }), _jsx(Button, { variant: statusFilter === 'approved' ? 'default' : 'outline', size: "sm", onClick: () => setStatusFilter('approved'), children: "Approved" }), _jsx(Button, { variant: statusFilter === 'rejected' ? 'default' : 'outline', size: "sm", onClick: () => setStatusFilter('rejected'), children: "Rejected" })] })] }), _jsxs(Card, { children: [_jsxs(CardHeader, { children: [_jsxs(CardTitle, { className: "flex items-center gap-2", children: [_jsx(Inbox, { className: "h-5 w-5" }), "Leave Requests"] }), _jsxs(CardDescription, { children: [filteredRequests.length, " request", filteredRequests.length !== 1 ? 's' : '', " found"] })] }), _jsx(CardContent, { children: filteredRequests.length === 0 ? (_jsxs("div", { className: "text-center py-12", children: [_jsx(Inbox, { className: "mx-auto h-12 w-12 text-gray-300 mb-3" }), _jsx("p", { className: "text-gray-600 font-medium", children: "No requests found" }), _jsx("p", { className: "text-sm text-gray-500 mt-1", children: statusFilter === 'all'
                                        ? 'No leave requests have been submitted yet'
                                        : `No ${statusFilter} requests` })] })) : (_jsx("div", { className: "space-y-2", children: Array.from(requestsByEmployee.entries()).map(([employeeId, requests]) => {
                                const isExpanded = expandedEmployees.has(employeeId);
                                const firstRequest = requests[0];
                                if (!firstRequest)
                                    return null;
                                return (_jsxs("div", { className: "border rounded-lg overflow-hidden", children: [_jsxs("button", { onClick: () => toggleEmployee(employeeId), className: "w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors", children: [_jsxs("div", { className: "flex items-center gap-3", children: [isExpanded ? (_jsx(ChevronDown, { className: "h-5 w-5 text-gray-400" })) : (_jsx(ChevronRight, { className: "h-5 w-5 text-gray-400" })), _jsx(User, { className: "h-5 w-5 text-gray-400" }), _jsxs("div", { className: "text-left", children: [_jsx("h3", { className: "font-semibold text-gray-900", children: firstRequest.employeeName }), _jsx("p", { className: "text-sm text-gray-500", children: firstRequest.employeePosition })] })] }), _jsxs(Badge, { variant: "secondary", children: [requests.length, " request", requests.length !== 1 ? 's' : ''] })] }), isExpanded && (_jsx("div", { className: "px-4 pb-4 border-t space-y-3", children: requests.map(request => {
                                                const StatusIcon = STATUS_ICONS[request.status];
                                                return (_jsxs("div", { className: "p-4 bg-gray-50 rounded-lg space-y-3", children: [_jsxs("div", { className: "flex items-start justify-between", children: [_jsxs("div", { className: "flex items-center gap-3", children: [_jsx(Calendar, { className: "h-5 w-5 text-gray-400" }), _jsxs("div", { children: [_jsx("div", { className: "font-medium text-gray-900", children: LEAVE_TYPE_LABELS[request.leaveType] }), _jsxs("div", { className: "text-sm text-gray-600", children: [format(parseISO(request.startDate), 'MMM d'), " - ", format(parseISO(request.endDate), 'MMM d, yyyy')] })] })] }), _jsxs(Badge, { className: STATUS_COLORS[request.status], children: [_jsx(StatusIcon, { className: "h-3 w-3 mr-1" }), request.status.charAt(0).toUpperCase() + request.status.slice(1)] })] }), _jsxs("div", { className: "grid grid-cols-2 gap-4 text-sm", children: [_jsxs("div", { children: [_jsx("span", { className: "text-gray-600", children: "Hours: " }), _jsxs("span", { className: "font-medium", children: [request.hoursRequested, " (", (request.hoursRequested / 8).toFixed(1), " days)"] })] }), _jsxs("div", { children: [_jsx("span", { className: "text-gray-600", children: "Submitted: " }), _jsx("span", { className: "font-medium", children: format(parseISO(request.createdAt), 'MMM d, yyyy') })] })] }), request.reason && (_jsxs("div", { className: "text-sm", children: [_jsx("span", { className: "text-gray-600", children: "Reason: " }), _jsx("span", { className: "text-gray-900", children: request.reason })] })), request.reviewedAt && (_jsx("div", { className: "text-sm pt-2 border-t", children: _jsxs("div", { className: "text-gray-600", children: ["Reviewed on ", format(parseISO(request.reviewedAt), 'MMM d, yyyy'), request.reviewNote && ` - ${request.reviewNote}`] }) }))] }, request.requestId));
                                            }) }))] }, employeeId));
                            }) })) })] })] }));
}
